import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Car, 
  Key, 
  Truck, 
  Wallet, 
  Play,
  ArrowLeft,
  CheckCircle,
  Globe,
  Shield,
  Zap
} from 'lucide-react';

export default function ExplorePage() {
  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_mercamove/artifacts/ch8knfdf_Move_Logo_wHT.png" 
                alt="Mercapoli Move"
                className="h-8"
              />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" className="text-slate-700 hover:text-[#00BFA6]">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver al inicio
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 font-['Outfit'] leading-tight">
            Descubre cómo funciona
            <span className="text-[#00BFA6] block">Mercapoli Move</span>
          </h1>
          <p className="text-xl text-slate-600 mt-6 leading-relaxed max-w-2xl mx-auto">
            Una super-app que integra movilidad, rentas y logística en una sola plataforma, 
            potenciada por el ecosistema DIGGII.
          </p>
        </div>
      </section>

      {/* Video Placeholder Section */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl overflow-hidden aspect-video shadow-2xl">
            {/* Video Placeholder */}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
              <div className="w-24 h-24 rounded-full bg-[#00BFA6]/20 flex items-center justify-center mb-6 backdrop-blur-sm border border-[#00BFA6]/30">
                <Play className="w-12 h-12 text-[#00BFA6] ml-1" />
              </div>
              <p className="text-2xl font-bold font-['Outfit'] mb-2">Muy pronto</p>
              <p className="text-slate-400 text-center max-w-md px-4">
                Conoce cómo funciona Mercapoli Move y el Ecosistema DIGGII
              </p>
            </div>
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-10 left-10 w-32 h-32 border border-white rounded-full" />
              <div className="absolute bottom-10 right-10 w-48 h-48 border border-white rounded-full" />
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 border border-white rounded-full" />
            </div>
          </div>
          <p className="text-center text-slate-500 text-sm mt-4">
            Video explicativo próximamente disponible
          </p>
        </div>
      </section>

      {/* Features Overview */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 font-['Outfit'] text-center mb-12">
            Todo en una sola app
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-[#F5F7FA] rounded-2xl p-6 text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 rounded-2xl bg-[#00BFA6]/10 flex items-center justify-center mx-auto mb-4">
                <Car className="w-8 h-8 text-[#00BFA6]" />
              </div>
              <h3 className="font-bold text-slate-800 mb-2">Ride-Hailing</h3>
              <p className="text-slate-600 text-sm">Viajes seguros con conductores verificados</p>
            </div>
            <div className="bg-[#F5F7FA] rounded-2xl p-6 text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 rounded-2xl bg-[#00887A]/10 flex items-center justify-center mx-auto mb-4">
                <Key className="w-8 h-8 text-[#00887A]" />
              </div>
              <h3 className="font-bold text-slate-800 mb-2">Renta de Vehículos</h3>
              <p className="text-slate-600 text-sm">Alquila por hora, día o semana</p>
            </div>
            <div className="bg-[#F5F7FA] rounded-2xl p-6 text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 rounded-2xl bg-[#FFD166]/10 flex items-center justify-center mx-auto mb-4">
                <Truck className="w-8 h-8 text-[#FFD166]" />
              </div>
              <h3 className="font-bold text-slate-800 mb-2">Fletes y Mandados</h3>
              <p className="text-slate-600 text-sm">Logística última milla confiable</p>
            </div>
            <div className="bg-[#F5F7FA] rounded-2xl p-6 text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 rounded-2xl bg-[#00BFA6]/10 flex items-center justify-center mx-auto mb-4">
                <Wallet className="w-8 h-8 text-[#00BFA6]" />
              </div>
              <h3 className="font-bold text-slate-800 mb-2">Digi Wallet</h3>
              <p className="text-slate-600 text-sm">Pagos digitales con tokens DIGGII</p>
            </div>
          </div>
        </div>
      </section>

      {/* DIGGII Ecosystem Info */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#00BFA6]/5 to-[#00887A]/5">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 font-['Outfit'] text-center mb-8">
            Ecosistema <span className="text-[#00BFA6]">DIGGII</span>
          </h2>
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#00BFA6]/10 flex items-center justify-center mx-auto mb-3">
                  <Globe className="w-6 h-6 text-[#00BFA6]" />
                </div>
                <h4 className="font-bold text-slate-800 mb-1">Multi-plataforma</h4>
                <p className="text-slate-600 text-sm">Compatible con todo el ecosistema Mercapoli</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#FFD166]/10 flex items-center justify-center mx-auto mb-3">
                  <Zap className="w-6 h-6 text-[#FFD166]" />
                </div>
                <h4 className="font-bold text-slate-800 mb-1">Recompensas</h4>
                <p className="text-slate-600 text-sm">Gana DIGGII por usar la plataforma</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#00887A]/10 flex items-center justify-center mx-auto mb-3">
                  <Shield className="w-6 h-6 text-[#00887A]" />
                </div>
                <h4 className="font-bold text-slate-800 mb-1">Seguro</h4>
                <p className="text-slate-600 text-sm">Transacciones protegidas 24/7</p>
              </div>
            </div>
            <div className="mt-8 pt-6 border-t border-slate-100">
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle className="w-5 h-5 text-[#00BFA6] flex-shrink-0" />
                  Gana DIGGII por cada viaje, renta o envío que realices
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle className="w-5 h-5 text-[#00BFA6] flex-shrink-0" />
                  Invita amigos y obtén recompensas progresivas
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle className="w-5 h-5 text-[#00BFA6] flex-shrink-0" />
                  Paga servicios parcial o totalmente con tus tokens
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-slate-900 font-['Outfit'] mb-4">
            ¿Listo para comenzar?
          </h2>
          <p className="text-slate-600 mb-8">
            Únete a miles de usuarios que ya disfrutan de Mercapoli Move
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button className="bg-[#00BFA6] hover:bg-[#00887A] text-white rounded-full h-12 px-8 text-lg font-semibold">
                Crear Cuenta Gratis
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-slate-300 text-slate-700 rounded-full h-12 px-8 text-lg">
                Volver al inicio
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-slate-100 border-t border-slate-200">
        <div className="max-w-4xl mx-auto text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_mercamove/artifacts/ch8knfdf_Move_Logo_wHT.png" 
            alt="Mercapoli Move"
            className="h-6 mx-auto mb-4"
          />
          <p className="text-slate-500 text-sm">
            © 2025 Mercapoli Move. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}
